/**
 * # SplitButton
 *
 * A component that renders a split button with two or more clickable elements.
 *
 * ## Props
 *
 * - `className` (optional): Additional CSS class name for the component.
 * - `children`: An array of two or more React elements representing the segments. Middle segments
 *   render square-cornered with a divider on their left.
 *
 * ## Usage
 *
 * ```jsx
 * import SplitButton from './SplitButton';
 *
 * const MyComponent = () => {
 *   return (
 *     <SplitButton className="my-split-button">
 *       <button>Button 1</button>
 *       <button>Button 2</button>
 *     </SplitButton>
 *   );
 * };
 * ```
 */

import React from 'react';
import classnames from 'classnames';

import './split-button.scss';

interface SplitButtonProps extends PropsWithClassNameOptional {
  /**
   * Two or more segments. The stylesheet rounds the outer ends and gives every segment after the
   * first a 1px left border, so a third child renders as a divided middle segment (for example an
   * arrow / dropdown / arrow control).
   */
  children: [React.ReactElement<any>, React.ReactElement<any>, ...React.ReactElement<any>[]];
}

const SplitButton = ({className, children, ...rest}: SplitButtonProps) => {
  return (
    <div className={classnames('split-button__wrapper', className)} {...rest}>
      {children}
    </div>
  );
};

SplitButton.displayName = 'SplitButton';

export default SplitButton;
